import { TokenAmount, Pair, Currency, Token } from '@uniswap/sdk'
import { AddressZero } from '@ethersproject/constants'
import { useMemo } from 'react'
import { abi as IUniswapV2PairABI } from '@uniswap/v2-core/build/IUniswapV2Pair.json'
import { Interface } from '@ethersproject/abi'
import { useActiveWeb3React } from '../hooks'
import { useV2FactoryContract } from '../hooks/useContract'

import { useMultipleContractSingleData, useSingleContractMultipleData } from '../state/multicall/hooks'
import { wrappedCurrency } from '../utils/wrappedCurrency'

const PAIR_INTERFACE = new Interface(IUniswapV2PairABI)

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID
}

export function usePairs(currencies: [Currency | undefined, Currency | undefined][]): [PairState, Pair | null][] {
  const { chainId } = useActiveWeb3React()
  const factoryContract = useV2FactoryContract()

  const tokens = useMemo(
    () =>
      currencies.map(([currencyA, currencyB]) => [
        wrappedCurrency(currencyA, chainId),
        wrappedCurrency(currencyB, chainId)
      ]),
    [chainId, currencies]
  )

  const pairAddressResults = useSingleContractMultipleData(
    factoryContract,
    'getPair',
    tokens.map(([tokenA, tokenB]) => {
      return tokenA && tokenB && !tokenA.equals(tokenB) ? [tokenA.address, tokenB.address] : undefined
    })
  )

  const pairAddresses = useMemo(
    () =>
      tokens.map((_, index) => {
        const result = pairAddressResults[index]
        const pairAddress = result.result?.[0]
        return pairAddress && pairAddress !== AddressZero ? pairAddress : undefined
      }),
    [pairAddressResults, tokens]
  )

  const results = useMultipleContractSingleData(pairAddresses, PAIR_INTERFACE, 'getReserves')

  return useMemo(() => {
    return tokens.map((tokenPair, i) => {
      const result = results[i]
      const reserves = result?.result
      const loading = result?.loading
      const tokenA = tokenPair[0]
      const tokenB = tokenPair[1]
      const pairAddress = pairAddresses[i]
      const loadingPairAddress = pairAddressResults[i]?.loading

      if (!tokenA || !tokenB || tokenA.equals(tokenB)) return [PairState.INVALID, null]
      if (!factoryContract) return [PairState.NOT_EXISTS, null]
      if (!result || loading || loadingPairAddress) return [PairState.LOADING, null]
      if (!pairAddress || !reserves) return [PairState.NOT_EXISTS, null]
      const { reserve0, reserve1 } = reserves
      const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
      const pair = new Pair(new TokenAmount(token0, reserve0.toString()), new TokenAmount(token1, reserve1.toString()))
      Object.defineProperty(pair, 'liquidityToken', {
        value: new Token(token0.chainId, pairAddress, 18, 'UNI-V2', 'Uniswap V2')
      })
      return [
        PairState.EXISTS,
        pair
      ]
    })
  }, [factoryContract, pairAddressResults, pairAddresses, results, tokens])
}

export function usePair(tokenA?: Currency, tokenB?: Currency): [PairState, Pair | null] {
  return usePairs([[tokenA, tokenB]])[0]
}
